import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CustomSnackbar from "../SnackBar";
import useSnackbar from "../SnackbarHook";

import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  CircularProgress,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Edit, Delete } from "@mui/icons-material";

import {
  fetchMeetings,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  Meeting,
  openMeetingForm,
  setMeeting,
  sendMeetingInvites,
} from "../../../redux/slices/meeting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";
import { usePermission } from "../../../hooks/usePermission";
import { format } from "date-fns";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  fetchMeetingInvitesByMeetingId,
  patchInvite,
} from "../../../redux/slices/meetingInvite";
import { a11yProps } from "../CustomTabs";
import MeetingInvite from "./MeetingInvite";
import { baseUrl } from "../../../config";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Aihe on pakollinen"),
  date: Yup.string().required("Päivämäärä on pakollinen"),
});

interface MeetingFormValues {
  title: string;
  date: string;
  agenda: string;
  submit?: string;
  place: string;
  type: string;
  associationId: number | null;
}

function MeetingForm({
  onComplete,
  onSaveAndCreateInvite,
  setOpenSnackbar,
  setSnackbarMessage,
  setSnackbarSeverity,
}: {
  onComplete: (success: boolean) => void;
  onSaveAndCreateInvite: (meetingId: number) => void;
  setOpenSnackbar: (isOpen: boolean) => void;
  setSnackbarMessage: (message: string) => void;
  setSnackbarSeverity: (
    severity: "success" | "error" | "info" | "warning"
  ) => void;
}) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { meeting } = useAppSelector((state) => state.meeting);
  const esRef = useRef<EventSource | null>(null);
  const finishedIdsRef = useRef<Set<number>>(new Set()); // de-dup
  const expectedCountRef = useRef<number>(0);

  const associationId = currentUser?.activeAssociation?.id;
  const [value, setValue] = useState(0);

  // const finalRefresh = async (meetingId: number) => {
  //   // canonical state from DB
  //   console.log("does it call this");
  //   await dispatch(fetchMeetingInvitesByMeetingId(meetingId));
  // };

  const resetBatchCounters = (recipients: number) => {
    finishedIdsRef.current = new Set();
    expectedCountRef.current = recipients;
  };

  const maybeFinalize = async (meetingId: number, es: EventSource) => {
    if (finishedIdsRef.current.size >= expectedCountRef.current) {
      await dispatch(fetchMeetingInvitesByMeetingId(meetingId)); // final DB sync
      es.close();
      if (esRef.current === es) esRef.current = null;
    }
  };

  // open SSE for a batch
  const openInviteStream = async (
    batchId: string,
    streamUrl: string,
    meetingId: number,
    recipients: number
  ) => {
    resetBatchCounters(recipients);

    // Load snapshot first
    await dispatch(fetchMeetingInvitesByMeetingId(meetingId));

    // Open SSE
    if (esRef.current) esRef.current.close();
    const url = streamUrl.startsWith("http")
      ? streamUrl
      : `${baseUrl}/meeting-invites/batch/${batchId}/stream`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("open", () => {
      setSnackbarMessage("Kutsujen lähetys käynnissä…");
      setSnackbarSeverity("info");
      setOpenSnackbar(true);
    });

    // Live patch for progress
    es.addEventListener("progress", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.inviteId) return;

      // Patch UI
      dispatch(
        patchInvite({
          inviteId: msg.inviteId,
          updates: {
            status: msg.status,
            pct: msg.pct,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      // Some backends emit "sent" as a progress update; treat that as terminal but only if from email
      if (msg.source === "email" && msg.status === "sent") {
        if (!finishedIdsRef.current.has(msg.inviteId)) {
          finishedIdsRef.current.add(msg.inviteId);
          maybeFinalize(meetingId, es);
        }
      }
    });

    // Count only parent/email completion as terminal
    es.addEventListener("completed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.inviteId) return;

      // Ignore file child completion
      if (msg.source !== "email") return;

      // Patch to sent (if not already)
      dispatch(
        patchInvite({
          inviteId: msg.inviteId,
          updates: {
            status: msg.status ?? "sent",
            sentAt: msg.sentAt ?? new Date().toISOString(),
            pct: 100,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.inviteId)) {
        finishedIdsRef.current.add(msg.inviteId);
        maybeFinalize(meetingId, es);
      }
    });

    es.addEventListener("failed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.inviteId) return;

      // Patch to failed
      dispatch(
        patchInvite({
          inviteId: msg.inviteId,
          updates: {
            status: "failed",
            lastError: msg.reason ?? msg.lastError ?? "Tuntematon virhe",
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.inviteId)) {
        finishedIdsRef.current.add(msg.inviteId);
        maybeFinalize(meetingId, es);
      }
    });

    es.addEventListener("ping", () => {});
    es.addEventListener("error", () => {
      // EventSource will auto-reconnect; optionally log
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSubmit = async (
    values: MeetingFormValues,
    formikHelpers: FormikHelpers<MeetingFormValues>
  ) => {
    const { resetForm, setErrors, setStatus, setSubmitting } = formikHelpers;

    try {
      const payload = {
        ...values,
        associationId,
      };

      if (meeting?.id !== undefined) {
        await dispatch(
          updateMeeting({ id: meeting.id, updatedMeeting: payload })
        );
      } else {
        await dispatch(createMeeting(payload));
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openMeetingForm(false));
      onComplete(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Virhe tallennettaessa kokousta.";

      setErrors({ submit: errorMessage });
      setStatus({ sent: false });
      setSubmitting(false);
      onComplete(false);
    }
  };

  const handleSaveAndRedirect = async (
    values: MeetingFormValues,
    formikHelpers: FormikHelpers<MeetingFormValues>
  ) => {
    const { setErrors, setStatus, setSubmitting } = formikHelpers;

    try {
      const payload = {
        ...values,
        associationId,
      };

      const result =
        meeting?.id !== undefined
          ? await dispatch(
              updateMeeting({ id: meeting.id, updatedMeeting: payload })
            ).unwrap()
          : await dispatch(createMeeting(payload)).unwrap();

      dispatch(setMeeting(result));

      setStatus({ sent: true });
      setSubmitting(false);

      if (typeof result.id === "number") {
        onSaveAndCreateInvite(result.id);
        setValue(1);
        const confirmed = window.confirm(
          "Haluatko lähettää sähköpostikutsut heti kokouksen tallennuksen jälkeen?"
        );
        if (confirmed) {
          try {
            const { batchId, recipients, streamUrl } = await dispatch(
              sendMeetingInvites({
                associationId: associationId,
                meetingId: result.id,
                place: values.place,
              })
            ).unwrap();

            await openInviteStream(batchId, streamUrl, result.id, recipients);
            // Also switch to the "Näytä kutsut" tab (you already do setValue(1))
            //await dispatch(fetchMeetingInvitesByMeetingId(result.id));

            setSnackbarMessage(
              `Kutsujen lähetys aloitettu (${recipients} vastaanottajaa).`
            );
            setSnackbarSeverity("success");
          } catch (e) {
            setSnackbarMessage("Virhe kutsujen lähetyksessä.");
            setSnackbarSeverity("error");
          } finally {
            setOpenSnackbar(true);
          }
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Virhe tallennettaessa kokousta.";

      setErrors({ submit: errorMessage });
      setStatus({ sent: false });
      setSubmitting(false);
      onComplete(false);
    }
  };

  const templateAgenda = `1. Kokouksen avaus.

2. Valitaan kokouksen puheenjohtaja, sihteeri, kaksi pöytäkirjantarkastajaa ja kaksi ääntenlaskijaa.

3. Todetaan kokouksen laillisuus ja päätösvaltaisuus.

4. Hyväksytään esityslista.

5. Esitetään toimintakertomus, tilinpäätös ja tilintarkastajien lausunto.

6. Päätetään tilinpäätöksen vahvistamisesta ja vastuuvapauden myöntämisestä hallitukselle.

7. Vahvistetaan toimintasuunnitelma, tulo- ja menoarvio sekä liittymis- ja jäsenmaksujen suuruudet.

8. Valitaan hallituksen puheenjohtaja.

9. Valitaan hallituksen muut jäsenet.

10. Valitaan tilintarkastaja ja varatilintarkastaja.

11. Käsitellään muut esille tulevat asiat.

12. Kokouksen päättäminen.`;

  // close SSE when component unmounts or when you navigate away
  useEffect(() => {
    return () => {
      if (esRef.current) esRef.current.close();
    };
  }, []);

  if (!associationId) {
    return (
      <Card mb={6}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="30vh"
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Formik<MeetingFormValues>
      initialValues={{
        title: meeting?.title || "",
        date: meeting?.date
          ? format(new Date(meeting.date), "yyyy-MM-dd'T'HH:mm")
          : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        agenda: meeting?.agenda || templateAgenda,
        place: "",
        type: meeting?.type || "annual",
        associationId: meeting?.associationId || null,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikHelpers) => {
        const {
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        } = formikHelpers;

        const handleSaveAndRedirectWrapper = () =>
          handleSaveAndRedirect(values, formikHelpers);

        return (
          <Card mb={6}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={value}
                    onChange={handleTabChange}
                    aria-label="meeting tabs"
                  >
                    <Tab
                      label={meeting ? "Muokkaa kokousta" : "Lisää uusi kokous"}
                      {...a11yProps(0)}
                    />
                    <Tab label="Kokouskutsut" {...a11yProps(1)} />
                  </Tabs>
                </Box>
              </Box>
              {value === 0 && (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={6}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="title"
                        label="Aihe"
                        value={values.title}
                        error={Boolean(touched.title && errors.title)}
                        fullWidth
                        helperText={touched.title && errors.title}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DateTimePicker
                        label="Päivämäärä ja aika"
                        ampm={false}
                        value={values.date ? new Date(values.date) : null}
                        onChange={(newValue) => {
                          setFieldValue("date", newValue?.toISOString() || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="date"
                            onBlur={handleBlur}
                            error={Boolean(touched.date && errors.date)}
                            helperText={touched.date && errors.date}
                            fullWidth
                            variant="outlined"
                            my={2}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        name="type"
                        label="Tyyppi"
                        select
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.type && errors.type)}
                        helperText={touched.type && errors.type}
                        fullWidth
                        variant="outlined"
                        my={2}
                      >
                        <MenuItem value="annual">Vuosikokous</MenuItem>
                        <MenuItem value="extraordinary">Ylimääräinen</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="place"
                        label="Paikka"
                        value={values.place}
                        error={Boolean(touched.place && errors.place)}
                        fullWidth
                        helperText={touched.place && errors.place}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="agenda"
                        label="Esityslista"
                        value={values.agenda}
                        error={Boolean(touched.agenda && errors.agenda)}
                        fullWidth
                        multiline
                        rows={20}
                        helperText={touched.agenda && errors.agenda}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                  </Grid>

                  {errors.submit && (
                    <Typography color="error" mt={2}>
                      {errors.submit}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    mt={3}
                    mr={2}
                    disabled={isSubmitting}
                  >
                    Tallenna
                  </Button>

                  <Button
                    variant="contained"
                    color="secondary"
                    mt={3}
                    mr={2}
                    disabled={isSubmitting}
                    onClick={handleSaveAndRedirectWrapper}
                  >
                    Tallenna ja luo kutsu
                  </Button>

                  <Button
                    variant="contained"
                    color="error"
                    mt={3}
                    onClick={() => dispatch(openMeetingForm(false))}
                  >
                    Peruuta
                  </Button>
                </form>
              )}
              {value === 1 && meeting?.id && (
                <MeetingInvite meetingId={meeting.id} hideHeader />
              )}

              {value === 1 && !meeting?.id && (
                <Typography variant="body1" sx={{ mt: 6 }}>
                  Tallenna kokous ennen kuin voit nähdä kutsut.
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      }}
    </Formik>
  );
}

function MeetingList(props: {
  rows: Meeting[];
  handleEditMeeting: (meeting: Meeting | null, isOpen: boolean) => void;
  handleDelete: (meeting: Meeting) => void;
}) {
  const { currentUser } = useAuth();
  const { status = "idle" } = useAppSelector((state) => state.meeting || {});
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Check permissions using RBAC
  const { hasPermission: canDeleteMeeting } = usePermission(
    "meeting",
    "delete",
    associationId
  );

  const [sortField, setSortField] = useState<"title" | "date">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (field: "title" | "date") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortArrow = (field: "title" | "date") => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const sortedRows = [...(props.rows || [])].sort((a, b) => {
    const aVal = a[sortField]?.toLowerCase?.() || "";
    const bVal = b[sortField]?.toLowerCase?.() || "";
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Kokoukset
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={"small"}
          onClick={() => props.handleEditMeeting(null, true)}
        >
          Lisää
        </Button>
      </CardContent>
      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Aihe{getSortArrow("title")}
              </TableCell>
              <TableCell
                onClick={() => handleSort("date")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Päivämäärä{getSortArrow("date")}
              </TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Ei kokouksia
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: Meeting) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    {format(new Date(row.date), "dd.MM.yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Edit
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleEditMeeting(row, true)}
                    />{" "}
                    {canDeleteMeeting ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleDelete(row)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Meetings() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { meetings, open } = useAppSelector((state) => state.meeting);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleSaveAndCreateInvite = (meetingId: any) => {
    // You might not need this navigation logic if you're handling it with tabs
    // but I'll keep it here for context.
    // navigate(`/meetings/meeting-invites/${meetingId}`, {
    //   state: { meetingId },
    // });
    setSnackbarMessage("Kokous tallennettu ja kutsujen luonti aloitetaan.");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  const handleDelete = async (meeting: Meeting) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa kokouksen "${meeting.title}"?`
    );
    if (confirmed && meeting.id) {
      setProgress(100);
      try {
        await dispatch(deleteMeeting(meeting.id));
        setSnackbarMessage("Kokous poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa kokousta.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleEditMeeting = (meeting: Meeting | null, isOpen: boolean) => {
    dispatch(setMeeting(meeting));
    dispatch(openMeetingForm(isOpen));
  };

  useEffect(() => {
    if (associationId) {
      dispatch(fetchMeetings(associationId));
    }
    return () => {
      dispatch(openMeetingForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Kokoukset" />
      <Typography variant="h3" gutterBottom display="inline">
        Kokoukset
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Kokoukset</Typography>
      </Breadcrumbs>

      <Divider my={6} />
      {open ? (
        <MeetingForm
          onComplete={(success: boolean) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Kokous tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa kokousta.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
          setOpenSnackbar={setOpenSnackbar}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
          onSaveAndCreateInvite={handleSaveAndCreateInvite}
        />
      ) : (
        <MeetingList
          rows={meetings}
          handleEditMeeting={handleEditMeeting}
          handleDelete={handleDelete}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Meetings;
